import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Suspense, lazy, useCallback, useState, useEffect } from 'react';
import { Page, Layout } from '@shopify/polaris';
import { useAuth } from '../hooks/useAuth'; // 获取用户认证状态
import { useTranslation } from 'react-i18next';
// import AppRoutesStyles from './AppRoutes.module.scss'; // 导入样式
import PageLoading from '../components/PageLoading'; // 导入页面加载组件
import ShopifyMenu from '../components/Shopify/ShopifyMenu/ShopifyMenu';
import { DataInitializer } from '../components/DataInitialier/DataInitialier';
import { useAppBridge } from '@shopify/app-bridge-react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../store';
import type { RootState } from '../store';
import { cancelChanges } from '../store/modules/chatSettingSlice';
import ShopifyModal from '../components/Shopify/ShopifyModal/ShopifyModal';
import UpdateModal from '@/components/UpdateModal/UpdateModal';

const lazyLoad = (path: string) => {
  return lazy(() => {
    return import(`@/${path}`); // 使用与 rsbuild.config.ts 中一致的路径别名
  });
};

// 使用 React.lazy 进行懒加载的组件
const Auth = lazyLoad('features/auth/Auth'); // 权限界面
const Home = lazyLoad('features/home/HomeContainer'); // 首页
const Chat = lazyLoad('features/chat/ChatContainer'); // 聊天界面
const FAQ = lazyLoad('features/faq/FAQContainer'); // 帮助中心
const QAEdit = lazyLoad('features/faq/childs/QAEditor'); // 问答编辑
const QASetting = lazyLoad('features/faq/childs/QASetting'); // 帮助中心设置
const WidgetSetting = lazyLoad('features/widget/WidgetContainer'); // 聊天设置
const Setting = lazyLoad('features/settings/SettingContainer'); // 系统设置
const NotFound = lazyLoad('components/NotFound/NotFound'); // 404
const Test = lazyLoad('features/test/test'); // 测试
const Template = lazyLoad('features/test/template'); // 测试

// 路由守卫组件 - 检查是否有未保存的内容
const RouteGuard = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const shopify = useAppBridge();
  const dispatch = useAppDispatch();
  const [pendingLocation, setPendingLocation] = useState<string | null>(null);
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);

  // 从Redux获取未保存状态
  const { hasUnsavedChanges } = useSelector((state: RootState) => {
    return state.chatSetting;
  });

  // 处理路由变化
  const handleRouteChange = useCallback((newPath: string) => {
    if (hasUnsavedChanges && 
        location.pathname.includes('/widget-setting') && 
        !newPath.includes('/widget-setting')) {
      // 有未保存的内容变更，显示确认弹窗
      setPendingLocation(newPath);
      setShowModal(true);
      return false;
    }
    
    // 没有未保存内容，允许路由跳转
    return true;
  }, [hasUnsavedChanges, location.pathname]);

  // 处理确认导航
  const handleConfirmNavigation = async () => {
    // 隐藏模态框
    setShowModal(false);
    
    // 取消所有未保存的更改
    await dispatch(cancelChanges());
    
    // 导航到目标页面
    if (pendingLocation) {
      navigate(pendingLocation);
      setPendingLocation(null);
    }
  };

  // 处理取消导航
  const handleCancelNavigation = () => {
    setShowModal(false);
    setPendingLocation(null);
  };

  // 监听浏览器的前进后退按钮
  useEffect(() => {
    // 复写 window.history.pushState 方法
    const originalPushState = window.history.pushState;
    
    window.history.pushState = (state, title, url) => {
      if (typeof url === 'string' && handleRouteChange(url)) {
        originalPushState.call(window.history, state, title, url);
      }
    };
    
    // 监听 popstate 事件（浏览器前进/后退按钮）
    const handlePopState = (event: PopStateEvent) => {
      if (hasUnsavedChanges && location.pathname.includes('/widget-setting')) {
        event.preventDefault();
        const newPath = window.location.pathname;
        setPendingLocation(newPath);
        setShowModal(true);
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.history.pushState = originalPushState;
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges, location.pathname, handleRouteChange]);

  return (
    <>
      {children}
      {/* 未保存内容确认弹窗 - 使用自定义 ShopifyModal */}
      <ShopifyModal
        id="route-unsaved-changes-modal"
        title={t('tips.unsavedChangesTitle')}
        open={showModal}
        onClose={handleCancelNavigation}
        primaryButton={{
          content: t('tips.confirmLeave'),
          onAction: handleConfirmNavigation,
          primary: true
        }}
        secondaryButtons={[
          {
            content: t('common.cancel'),
            onAction: handleCancelNavigation
          }
        ]}
      >
        <p>{t('tips.unsavedChangesLeaveMessage')}</p>
        <p>{t('tips.confirmLeaveQuestion')}</p>
      </ShopifyModal>
    </>
  );
};

// 私有路由组件，用于保护需要认证的路由
const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuth(); // 使用自定义钩子获取认证状态
  return isAuthenticated ? (
    <DataInitializer>
      <RouteGuard>
        {children}
      </RouteGuard>
    </DataInitializer>
  ) : (
    <Navigate to="/auth" />
  );
};

// 定义懒加载时的加载指示器 - Auth页面使用默认Loading
const AuthLoadingFallback = <PageLoading message="Loading..." showLogo={true} />;

// 主路由组件
const AppRoutes: React.FC = () => {

  // 已认证用户的路由内容
  const routeContent = (
    <Routes>
      {/* 首页 */}
      {/* 权限页面 获取用户数据并且存起来 */}
      <Route path="/" element={<Suspense fallback={AuthLoadingFallback}><Auth /></Suspense>} />
      <Route path="/auth" element={<Suspense fallback={AuthLoadingFallback}><Auth /></Suspense>} />

      <Route path="/home" element={<PrivateRoute><Home /></PrivateRoute>} />
      
      {/* Chat模块 */}
      {/* <Route path="/chat" element={<PrivateRoute><Chat /></PrivateRoute>} /> */}
      
      {/* FAQ模块 */}
      <Route path="/faq" element={<PrivateRoute><FAQ /></PrivateRoute>} />
      <Route path="/faq/edit" element={<PrivateRoute><QAEdit /></PrivateRoute>} />
      <Route path="/faq/setting" element={<PrivateRoute><QASetting /></PrivateRoute>} />
      
      {/* 设置模块 */}
      <Route path="/widget-setting" element={<PrivateRoute><WidgetSetting /></PrivateRoute>} />
      <Route path="/setting/*" element={<PrivateRoute><Setting /></PrivateRoute>} />
      
      {/* 测试页面 */}
      {/* <Route path="/test" element={<PrivateRoute><Test /></PrivateRoute>} /> */}
      {/* <Route path="/template" element={<PrivateRoute><Template /></PrivateRoute>} /> */}
      
      {/* 404页面 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );

  return (
    <Router>
      <ShopifyMenu />
      <Page fullWidth>
        <Layout>
          {/* 主内容区域 */}
          <Layout.Section>
            <Suspense fallback={<PageLoading />}>
              {routeContent}
            </Suspense>
          </Layout.Section>
        </Layout>
      </Page>
      <UpdateModal />
    </Router>
  );
};

export default AppRoutes;
